import { Component, Inject, OnDestroy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { get, cloneDeep, assignIn, find, round, floor } from 'lodash';
import { Observable, Subject } from 'rxjs';
import { tap } from 'rxjs/operators';

import { AppService } from 'src/app/app.service';
import { OrderService } from 'src/app/_services/order.service';
import { GiftcardService } from './giftcard.service';

@Component({
    selector: 'app-giftcard-dialog',
    templateUrl: './giftcard-dialog.component.html',
    styleUrl: './giftcard-dialog.component.scss',
    standalone: false
})
export class GiftcardDialogComponent implements OnDestroy {
	$storage: any;

	loading: boolean = true;
	args: any;
	giftCard: any;
	maxAmount: number = 0;
	otpMessage: string;
	otpMessageNumber: string;
	countdownValue$ : Observable<number>;
	countdownDone: boolean = false;

	private destroy$ = new Subject();

	constructor(
		public appService: AppService,
		public dataService: OrderService,
		public giftcardService: GiftcardService,
		public dialogRef: MatDialogRef<GiftcardDialogComponent>,
		@Inject(MAT_DIALOG_DATA) public data: any
	) {
		this.$storage = this.dataService.$storage;
		this.maxAmount = this.data.maxAmount;
		this.args = {
			title: '_GIFTCARD.select_card',
			actionTitle: 'continue',
			mode: 'set',
			denomination: get(this.$storage, 'rosConfig.currencySettings.minimalDiscountDenomination', 1),
			roundTo: (() => {
				const denomination = get(this.$storage, 'rosConfig.currencySettings.minimalDiscountDenomination', 1);
				switch (denomination) {
					case 1: return 2;
					case 10: return 1;
					case 100: return 0;
					default: return 2;
				}
			})()
		}
		if (this.data.giftCard) {
			this.loading = false;
			this.giftCard = cloneDeep(this.data.giftCard);
			this.setAmountMode();
		} else {
			if(this.data.account) {
				this.selectAccount(this.data.account);
				if (this.giftCard.pan && this.args.mode == 'select') {
					this.checkGiftCard();
				} else {
					this.loading = false;
				}
			} else {
				const accounts = this.$storage.giftCards.accounts;
				if (accounts.length == 1) {
					this.selectAccount(accounts[0]);
					if (this.giftCard.pan && this.args.mode == 'select') {
						this.checkGiftCard();
					} else {
						this.loading = false;
					}
				}
				else if (accounts.length > 1) {
					let account = this.$storage.giftCards.accounts.find(x => x.loyaltyApiKey == this.$storage.loyaltyClub.accountGuid);
					this.selectAccount(account);
					if (this.giftCard.pan && this.args.mode == 'select') {
						this.checkGiftCard();
					} else {
						this.loading = false;
					}
				} else {
					this.loading = false;
				}
			}
		}
		if (["US", "AU"].includes(this.appService.region)) this.giftCard.hideCVV = true;
	}

	ngOnDestroy(): void {
		//this.destroy$.next();
		//this.destroy$.complete();
	}

	selectAccount(account) {
		this.giftCard = { paymentMethod: 'giftCard' };
		this.args.mode = 'select';
		assignIn(this.giftCard, {
			$$loyaltyApiKey: account.loyaltyApiKey,
			$$accountName: account.name,
			account: account._id,
			pan: this.getGiftCardPan(),
			hideCVV: ["IL"].includes(this.appService.region),
			hideOTP: true
		});
		this.args.title = this.giftCard.$$accountName;
	}

	apply(form) {
		if (!this.giftCard) {
			return this.appService.alert({content:this.appService.translate('_GIFTCARD.please_select_card')});
		}
		if (!form.valid) return;
		if (this.args.mode == 'select') this.checkGiftCard();
		else this.applyGiftCard();
	}

	checkGiftCard(resendVerificationCode?) {
		this.loading = true;
		if (resendVerificationCode) {
			this.giftCard.otp = '';
		}
		else if (find(this.$storage.giftCards?.cards, { pan: this.giftCard.pan }) && this.giftCard?.paymentMethod !== 'giftCard') {
			this.loading = false;
			return this.appService.alert({content:this.appService.translate('_GIFTCARD.inuse_error')});
		}
		this.giftcardService.checkGiftCard(this.giftCard)
			.then((res: any) => {
				this.loading = true;
				this.giftCard.balance = res.balance;
				if(this.maxAmount > this.giftCard.balance) this.maxAmount = this.giftCard.balance;
				this.setAmountMode(true);
				this.giftCard.hideOTP = true;
			}).catch(err => {
			this.loading = false;
			let message = '_GIFTCARD.notfound_error';
			switch (err.Key) {
				case 'ExternalProviderErrror': message = err.Message; break;
				case 'CannotPayOnOnline': message = '_GIFTCARD.cannotPayOnOnline'; break;
				case 'incorrectCvv': message = '_GIFTCARD.invalid_cvv'; break;
				case 'auth_otpSent':
					this.otpMessage = this.appService.translate('_GIFTCARD.otp_required');
					let last3Digits = get(err, 'ResponseData.last3Digits');
					if (last3Digits && last3Digits.length) {
						this.otpMessage = this.appService.translate('_GIFTCARD.otp_required_alt');
						this.otpMessageNumber = this.appService.translate('_GIFTCARD.otp_required_alt_num', { v: last3Digits });
					}
					this.startResendCodeCountdown();
					this.giftCard.hideOTP = false;
					return;
				case 'InvalidPinCode':
					message = '_GIFTCARD.invalid_otp';
					break;
				case 'CannotCreateNewPinCode':
					message = 'MESSAGES.TOO_MANY_CODE_REQUESTS';
					break;
				case 'cvvRequired':
					message = '_GIFTCARD.cvv_required';
					if (this.giftCard.hideCVV) {
						this.giftCard.hideCVV = false;
						return;
					}
					break;
				case 'balance': message = '_GIFTCARD.balance_error'; break;
			}
			if (["IL"].includes(this.appService.region) || err.Key !== 'cvvRequired') {
				this.appService.alert({content: this.appService.translate(message, { balance: err.balance }) }).then();
			}
		}).then(() => {
			this.loading = false;
		});
	}

	applyGiftCard() {
		this.loading = true;
		let amount = this.giftCard.amount;
		if (isNaN(amount) || amount > this.args.maxAmount || amount < 1) {
			this.loading = false;
			return;
		}
		amount = floor(amount, this.args.roundTo);
		this.giftCard.amount = amount;

		if (this.data.giftCard) {
			assignIn(this.data.giftCard, this.giftCard);
			this.giftcardService.sanitizeGiftCards();
		} else {
			this.giftcardService.addGiftCard(this.giftCard);
		}
		this.dialogRef.close(this.giftCard)
	}

	setAmountMode(isNew?) {
		this.args.mode = 'amount';
		let order = this.$storage.order;
		let maxAmount = this.maxAmount;
		if (this.data.giftCard) {
			this.args.actionTitle = '_GIFTCARD.edit_card';
			maxAmount += this.data.giftCard.amount;
		} else {
			this.args.actionTitle = '_GIFTCARD.add_card';
		}
		this.args.order = order;
		this.args.balanceDue = maxAmount;
		this.args.maxAmount = round(floor(Math.min(maxAmount, this.giftCard.balance), this.args.roundTo), 2);
		this.args.minT = { min: this.appService.parseCurrency(1) };
		this.args.maxT = { max: this.appService.parseCurrency(this.args.maxAmount) };
		if (isNew) this.giftCard.amount = this.args.maxAmount;
	}

	getGiftCardPan() {
		let prePaidGiftCard = this.$storage.giftCards?.cards?.find(card => [card.paymentType, card.paymentMethod].includes('giftCard'));
		if (!prePaidGiftCard) return this.$storage?.loyaltyData?.customer?.mobile;
		return null;
	}

	startResendCodeCountdown(): void {

	}

	get validCodeLength(): boolean {
		return !this.giftCard.hideOTP && this.giftCard?.otp?.length >= 4;
	}

	get panPattern(): string {
		let pattern = '';
		if (!["AU"].includes(this.appService.region) ) pattern = '[0-9]*';
		return pattern;
	}
}

